import type { Attempt, CategoryId, Question, Track } from './types';

export const EXAM_QUESTION_COUNT = 75;
export const EXAM_DURATION_MS = 90 * 60 * 1000;
export const EXAM_PASS_SCORE = 65;
export const CATEGORIES: CategoryId[] = ['PRZ', 'DRO', 'ZNA', 'LOC', 'MET', 'BUD', 'NAP', 'MAN', 'RAT', 'POM'];

export function seededRandom(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value += 0x6d2b79f5;
    let result = value;
    result = Math.imul(result ^ (result >>> 15), result | 1);
    result ^= result + Math.imul(result ^ (result >>> 7), result | 61);
    return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
  };
}

export function shuffle<T>(input: readonly T[], random = Math.random): T[] {
  const result = [...input];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(random() * (index + 1));
    [result[index], result[swap]] = [result[swap], result[index]];
  }
  return result;
}

export function selectExamQuestions(pool: Question[], track: Track, random = Math.random): Question[] {
  const available = pool.filter((question) => question.primary_objectives.some((objective) => objective.track === track));
  const extraCategories = new Set(shuffle(CATEGORIES, random).slice(0, 5));
  const selected: Question[] = [];
  const families = new Set<string>();

  for (const category of CATEGORIES) {
    const target = extraCategories.has(category) ? 8 : 7;
    const candidates = shuffle(available.filter((question) => question.category_id === category), random);
    for (const question of candidates) {
      if (families.has(question.family_id)) continue;
      selected.push(question);
      families.add(question.family_id);
      if (selected.filter((item) => item.category_id === category).length === target) break;
    }
    const count = selected.filter((item) => item.category_id === category).length;
    if (count < target) throw new Error('Za mało zatwierdzonych rodzin w kategorii ' + category + ': ' + count + '/' + target + '.');
  }
  return shuffle(selected, random);
}

export function createExam(pool: Question[], track: Track, bankVersion: string, now = Date.now(), random = Math.random): Attempt {
  const questions = selectExamQuestions(pool, track, random);
  return {
    id: crypto.randomUUID(),
    kind: 'exam',
    track,
    bankVersion,
    questions: questions.map((question) => ({
      questionId: question.id,
      revision: question.revision,
      familyId: question.family_id,
      optionOrder: shuffle(question.options.map((option) => option.id), random)
    })),
    answers: {},
    flagged: [],
    startedAt: new Date(now).toISOString(),
    endAt: new Date(now + EXAM_DURATION_MS).toISOString(),
    version: 1,
    syncState: 'local'
  };
}

export function finishAttempt(attempt: Attempt, questions: Map<string, Question>, now = Date.now()): Attempt {
  const score = attempt.questions.reduce((sum, item) => {
    const question = questions.get(item.questionId);
    return sum + (question && attempt.answers[item.questionId] === question.answer.correct_option_id ? 1 : 0);
  }, 0);
  return { ...attempt, score, passed: score >= EXAM_PASS_SCORE, completedAt: new Date(now).toISOString(), version: attempt.version + 1, syncState: 'pending' };
}

export function remainingMilliseconds(attempt: Attempt, now = Date.now()): number {
  if (!attempt.endAt) return 0;
  return Math.max(0, new Date(attempt.endAt).getTime() - now);
}
